import { isDefined, noop } from '@sgme/fp';
import classnames from 'classnames';
import type { IntlKey } from 'locales';
import type { MouseEvent } from 'react';
import { useCallback, useId, useMemo } from 'react';
import { useIntl } from 'react-intl';
import Skeleton from 'react-loading-skeleton';
import { camelCaseToWords } from 'utils/string';

export type IconMapper<T extends string = string> = (item: T) => string;
export type TranslationKeyMapper<T extends string = string> = (item: T) => string;

export interface ButtonsGroupProps<T extends string = string> {
  id?: string;
  e2e?: string;
  className?: string;
  items: readonly T[];
  onSelect?: (value: readonly T[]) => void;
  selectedItems: Nullable<T> | readonly Nullable<T>[];
  onSelectionChanged?: (value: readonly T[]) => void;
  onRemove?: (value: readonly T[]) => void;
  translationPrefix?: string;
  guidedTourStepPrefix?: string;
  isDisabled?: boolean;
  icon?: IconMapper<T>;
  translationKey?: TranslationKeyMapper<T>;
  isInvalid?: boolean;
  disabledItems?: readonly T[];
  isRadio?: boolean;
  blurOnClick?: boolean;
  translator?: (val: string) => string;
  shouldCamelCaseLabelOnDefaultMessage?: boolean;
  isReadonly?: boolean;
  readonlyItems?: readonly T[];
  hasRadioStyle?: boolean;
  allOption?: string;
  allOptionTranslationKey?: IntlKey;
}

interface OptionProps<T extends string = string> {
  item: T;
  isActive: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  isReadonly?: boolean;
  translationPrefix?: string;
  guidedTourStepPrefix?: string;
  translationKey?: TranslationKeyMapper<T>;
  icon?: IconMapper<T>;
  onSelect: (item: T) => void;
  blurOnClick?: boolean;
  translator?: (val: string) => string;
  shouldCamelCaseLabelOnDefaultMessage?: boolean;
  hasRadioStyle?: boolean;
  className?: string;
  itemIndex?: number;
  name?: string;
}

export const Option = <T extends string = string>({
  item,
  isActive,
  isDisabled,
  isInvalid,
  icon,
  translationKey,
  onSelect,
  translationPrefix,
  guidedTourStepPrefix,
  tabIndex,
  blurOnClick,
  translator,
  shouldCamelCaseLabelOnDefaultMessage = true,
  hasRadioStyle,
  isReadonly = false,
  className,
  itemIndex,
  name,
}: OptionProps<T> & { tabIndex?: number }) => {
  const id = useId();
  const { formatMessage } = useIntl();

  const onClick = useCallback(
    (e: MouseEvent<HTMLInputElement>) => {
      if (blurOnClick) {
        e.currentTarget.blur();
      }
      onSelect(item);
    },
    [item, onSelect, blurOnClick],
  );

  const label = useMemo(() => {
    if (translator) {
      return translator(item);
    }
    const fallbackLabel = formatMessage({
      id: `field.${item}` as IntlKey,
      defaultMessage: shouldCamelCaseLabelOnDefaultMessage ? camelCaseToWords(item) : item,
    });
    if (translationPrefix === undefined) {
      return fallbackLabel;
    }
    return formatMessage({
      id: `${translationPrefix}.${translationKey !== undefined ? translationKey(item) : item}` as IntlKey,
      defaultMessage: fallbackLabel,
    });
  }, [translationPrefix, translationKey, item, formatMessage, translator, shouldCamelCaseLabelOnDefaultMessage]);

  const guidedTourStep = useMemo(
    () =>
      guidedTourStepPrefix ? `${guidedTourStepPrefix}.${translationKey ? translationKey(item) : item}` : undefined,
    [guidedTourStepPrefix, item, translationKey],
  );

  const inputClassName = hasRadioStyle
    ? classnames('form-check-label')
    : classnames('btn', {
        active: isActive,
        'btn-icon-start': icon,
        'btn-toggle-primary': !isInvalid,
        'btn-toggle-warning': isInvalid,
      });

  return (
    <>
      <input
        type="radio"
        className={classnames(
          !hasRadioStyle && 'btn-check',
          hasRadioStyle && { 'form-check-input': true, 'is-invalid': isInvalid },
          className,
        )}
        id={id}
        onClick={onClick}
        onChange={noop} // Avoid React devmode log
        key={item + isActive} // Update checked attribute
        data-e2e={item}
        checked={isActive}
        tabIndex={tabIndex}
        readOnly={isReadonly}
        disabled={isDisabled}
        name={name}
        value={item}
      />
      <label
        htmlFor={id}
        className={classnames(inputClassName, className, { unclickable: isReadonly })}
        style={{ cursor: isReadonly ? 'default' : 'pointer' }}
        id={itemIndex ? itemIndex.toString() : undefined}
        data-tour={guidedTourStep}
      >
        {icon && <em className="icon icon-md me-2">{icon(item)}</em>}
        {label}
      </label>
    </>
  );
};

function isItemSelected<T>(selectedItems: ReadonlyArray<T | null>, item: T) {
  return selectedItems.some(selectedItem => item === selectedItem);
}

export const ButtonsGroup = <T extends string = string>({
  id,
  e2e,
  className,
  items,
  selectedItems,
  translationPrefix,
  guidedTourStepPrefix,
  isDisabled,
  icon,
  translationKey,
  onSelect,
  isInvalid,
  tabIndex,
  disabledItems = [],
  isRadio,
  onRemove,
  blurOnClick,
  translator,
  hasRadioStyle = false,
  shouldCamelCaseLabelOnDefaultMessage = true,
  isReadonly = false,
  onSelectionChanged,
  readonlyItems = [],
  allOption,
}: ButtonsGroupProps<T> & { tabIndex?: number }) => {
  const _selectedItems = useMemo(
    () => (Array.isArray(selectedItems) ? selectedItems : [selectedItems]),
    [selectedItems],
  );

  const _onSelect = useCallback(
    (value: T) => {
      const valueIdx = _selectedItems.indexOf(value);
      if (isRadio) {
        onSelect?.([value]);
        onSelectionChanged?.(valueIdx >= 0 ? [] : [value]);
        onRemove?.(items.filter(i => i !== value));
      } else {
        if (valueIdx >= 0) {
          onRemove?.([_selectedItems[valueIdx]!]);
          const items = [..._selectedItems];
          items.splice(valueIdx, 1);
          onSelectionChanged?.(items);
        } else {
          const items = [...(_selectedItems as T[]), value];
          onSelect?.(items);
          onSelectionChanged?.(items);
        }
      }
    },
    [_selectedItems, onSelect, onRemove, isRadio, items, onSelectionChanged],
  );

  const allItemsSelected = selectedItems?.length === items.length;

  const onSelectAll = useCallback(() => {
    if (allItemsSelected) {
      onSelectionChanged?.([]);
    } else {
      onSelectionChanged?.(items);
      if (onSelect) {
        items?.forEach(i => onSelect([i]));
      }
    }
  }, [onSelectionChanged, onSelect, items, allItemsSelected]);

  return (
    <div id={id} data-e2e={e2e} className={classnames({ ['btn-group btn-group-toggle']: !hasRadioStyle }, className)}>
      {items.filter(isDefined).map((item, i) => {
        const option = (
          <Option
            name={id}
            key={item}
            hasRadioStyle={hasRadioStyle}
            item={item}
            isActive={isItemSelected(_selectedItems, item)}
            isInvalid={isInvalid}
            isDisabled={isDisabled || disabledItems.includes(item)}
            isReadonly={isReadonly || readonlyItems.includes(item)}
            onSelect={_onSelect}
            translationPrefix={translationPrefix}
            guidedTourStepPrefix={guidedTourStepPrefix}
            translationKey={translationKey}
            tabIndex={tabIndex !== undefined ? tabIndex + i : undefined}
            icon={icon}
            blurOnClick={blurOnClick}
            translator={translator}
            shouldCamelCaseLabelOnDefaultMessage={shouldCamelCaseLabelOnDefaultMessage}
            itemIndex={i}
          />
        );
        if (hasRadioStyle) {
          return (
            <div className={'d-inline-flex form-check '} key={item}>
              {option}
            </div>
          );
        }
        return option;
      })}
      {allOption && !isRadio && (
        <Option
          name={id}
          className="mx-1"
          isActive={allItemsSelected}
          key={allOption}
          hasRadioStyle={hasRadioStyle}
          item={allOption}
          isDisabled={isDisabled}
          isReadonly={isReadonly}
          onSelect={onSelectAll}
          translationPrefix={translationPrefix}
          guidedTourStepPrefix={guidedTourStepPrefix}
          tabIndex={tabIndex !== undefined ? tabIndex + items.length : undefined}
          blurOnClick={blurOnClick}
          translator={translator}
          shouldCamelCaseLabelOnDefaultMessage={shouldCamelCaseLabelOnDefaultMessage}
        />
      )}
    </div>
  );
};

ButtonsGroup.displayName = 'ButtonsGroup';

export interface RadioButtonsSkeletonProps<T extends string = string> {
  id?: string;
  e2e?: string;
  className?: string;
  items: readonly T[];
}

export const RadioButtonsSkeleton = <T extends string = string>({
  id,
  e2e,
  className,
  items,
}: RadioButtonsSkeletonProps<T>) => {
  return (
    <div id={id} data-e2e={e2e} className={classnames('btn-group btn-group-toggle w-100', className)}>
      {items
        .filter((item: string) => item)
        .map((item: string) => (
          <button key={item} type="button" className="btn btn-toggle-primary" data-e2e={item} disabled>
            <Skeleton width={`${item.length}em`} />
          </button>
        ))}
    </div>
  );
};

RadioButtonsSkeleton.displayName = 'RadioButtonsSkeleton';
